import { capitalize, isEmpty, now, orderBy } from 'lodash-es';
import { DateTime } from 'luxon';

import has from '@dojo/framework/core/has';
import { WNode } from '@dojo/framework/core/interfaces';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { create, destroy, w } from '@dojo/framework/core/vdom';
import { MotivationCard, TGFCard } from '@holmescorp/card';
import { check_rpc_passed } from '@holmescorp/certificate';
import { convertToBoolean } from '@holmescorp/common';
import {
	ContentOrder,
	contentTrack,
	createNlsMiddleware,
	keymap,
	lccstate,
	motivatedDateChange,
	pathmap,
	recommended,
	studyPlanToolChange,
	tgfkeys,
	userstate
} from '@holmescorp/lcc-state';
import { TopicDetails } from '@holmescorp/lcc-state/interfaces';
import { subscribe } from '@holmescorp/pub-sub';
import { ResourceParser } from '@holmescorp/resource-parser';

import { FormattingReplacements } from '../interfaces';
import { QuizStateMap, StudyPlanExtraItem, StudyPlanItem, StudyPlanState } from '../interfaces';
import { LoadingCard } from '../support/cards/loading-card';
import { svc } from '../support/svc';

export interface StudyPlanDomain {
	domain: string;
	title: string;
	cards: WNode[];
	completed: number;
}

interface ConditionalTopicFilters {
	[key: string]: {
		filterby: string;
		condition: boolean;
	};
}

export interface StudyPlanIcache {
	earned: boolean;
}

export interface Tracked {
	studyplan: StudyPlanItem[];
	quizzes: QuizStateMap;
}

export interface StudyPlanAPI {
	tracked: Tracked;
	changes: StudyPlanItem[];
	motivation_cards_list: WNode[];
	extra_cards_list: WNode[];
	setup(): void;
	getDomains(): StudyPlanDomain[];
	getStudyPlan(keys: StudyPlanItem[]): WNode[];
	recalculate(recalculateQuizzes?: boolean): void;
	enqueue(key: string): void;
	dequeue(key: string): void;
	getTopic(key: string): TopicDetails;
	getQuizzes(this: StudyPlanAPI): Promise<QuizStateMap>;
	calculate_motivations(): void;
	calculate_milestones(): void;
	getMotivations(): WNode[];
	calculate_extras(changedItem?: StudyPlanExtraItem): void;
	getExtraCardInfo(): Record<string, unknown>;
	getExtraCards(): WNode[];
}

const factory = create({
	icache: createICacheMiddleware<StudyPlanIcache>(),
	nls: createNlsMiddleware('ditabook'),
	resources: createNlsMiddleware('resources'),
	destroy,
	lccstate,
	userstate,
	recommended,
	contentTrack
});

export const studyplan = factory(
	({ middleware: { icache, destroy, nls, resources, lccstate, userstate, recommended, contentTrack } }) => {
		const handles: any[] = [];
		destroy((): void => {
			let handle: any;
			while ((handle = handles.pop())) {
				handle();
			}
		});

		const { get, path } = userstate;
		const assessment = get(path('assessment'));

		// refresh data when a quiz is started or finalized
		const s1 = subscribe(
			'quiz:status-change',
			(
				topic: string,
				payload: {
					quizid: string;
					status?: string;
				}
			): void => {
				if (payload?.status === 'final') {
					api.getQuizzes();
				}
			}
		);
		handles.push((): void => s1.destroy());

		// Quiz Id
		const quizPattern = userstate.get(userstate.path('quizPattern'));
		const quizPatterns = lccstate.get(lccstate.path('quizpatterns'));
		const singleQuizPattern = Object.keys(quizPatterns).length <= 1;
		const hasQuizPattern = singleQuizPattern || (quizPattern !== undefined && quizPattern !== null);
		const isBasicQuiz = quizPattern === '' || singleQuizPattern;
		const getQuizId = (key: string): string => {
			return isBasicQuiz ? `${key}` : hasQuizPattern ? `${key}-${quizPattern}` : `${key}`;
		};

		const {
			messages: {
				conditional_topic_filters = '{}',
				conditional_filter_state_key = 'landing',
				use_motivation = 'false',
				use_milestone = 'false',
				motivation_days_threshold = '21',
				milestone_threshold = '7'
			}
		} = nls();
		const { messages: resourceMessages } = resources();

		let parsedFilters: ConditionalTopicFilters = {};
		try {
			parsedFilters = JSON.parse(conditional_topic_filters);
		} catch (e) {
			parsedFilters = {};
		}

		const use_motivation_card = convertToBoolean(use_motivation);
		const use_milestone_card = convertToBoolean(use_milestone);

		const lowScoreData = Object.assign(
			{},
			...recommended.tracked.recommended.map((tgf) => {
				const key = tgf.topicset;
				const pretestScore = assessment[key].data.percent;
				return { [key]: pretestScore };
			})
		);

		const mapKids = (kids: ContentOrder[]) => {
			return Object.assign({}, ...kids.map((tgf) => ({ [tgf.topicset]: tgf })));
		};

		const dedup = (items: StudyPlanItem[]) => {
			const keys = items.map((i) => i.key);
			return items.filter(({ key }, index) => !keys.includes(key, index + 1));
		};

		const filterConditionals = (tgf: ContentOrder) => {
			const toolstate = get(path('toolstate', conditional_filter_state_key));

			if (tgf.topicset in parsedFilters === false || !toolstate) {
				return true;
			}

			const filter = parsedFilters[tgf.topicset];
			return filter && toolstate[filter.filterby] && toolstate[filter.filterby] === filter.condition;
		};

		// API
		const api: StudyPlanAPI = {
			changes: [],
			tracked: {
				studyplan: [],
				quizzes: {}
			},
			motivation_cards_list: [],
			extra_cards_list: [],

			setup(this: StudyPlanAPI): void {
				this.recalculate();
				const store = userstate.getStore();
				const { path } = store;
				const handle = store.onChange(
					[path('status'), path('assessment'), path('recommended'), path('toolstate', 'landing')],
					() => {
						this.recalculate();
					}
				);
				handles.push(() => handle.remove());
			},

			recalculate(this: StudyPlanAPI): void {
				window.hcdata = window.hcdata || {};
				const { get, path } = userstate;
				const studyplan = get(path('toolstate', 'studyplan'));

				const buildStudyplan = () => {
					const r = recommended.tracked.recommended.filter(filterConditionals);

					const {
						messages: { pretest_indicator_threshold = '66.66' }
					} = nls();
					const scoreThreshold = parseFloat(pretest_indicator_threshold);

					const { items: spItems = [] } = studyplan;
					const current: StudyPlanItem[] = [...spItems];

					// changes
					const up = this.changes.filter((item) => item.state === 'promoted');
					const down = this.changes.filter((item) => item.state === 'demoted');

					// promoted
					const promoted = current.filter(
						(item) => item.state === 'promoted' && !down.map((item) => item.key).includes(item.key)
					);
					const unsorted = dedup([...promoted, ...up]);
					const upqueue = orderBy(unsorted, 'date', 'desc');

					// Demoted
					const demoted = current.filter(
						(item) => item.state === 'demoted' && !up.map((item) => item.key).includes(item.key)
					);
					const completed = r
						.filter(
							(item) =>
								item.isreadingcompleted &&
								item.isquizcompleted &&
								!up.map((item) => item.key).includes(item.topicset)
						)
						.map((item) => {
							const pretest: number = lowScoreData[item.topicset];
							return {
								key: item.topicset,
								title: item.title,
								state: 'demoted' as StudyPlanState,
								date: null,
								pretest
							};
						});
					const downqueue = dedup([...demoted, ...down, ...completed]);

					// recommended not in promoted
					const recommendedItems = r
						.map((item) => {
							const pretest: number = lowScoreData[item.topicset];
							return {
								key: item.topicset,
								title: item.title,
								state: 'queued' as StudyPlanState,
								date: null,
								pretest,
								recommendedorder: item.recommendedorder
							};
						})
						.filter((item) => !upqueue.map((p) => p.key).includes(item.key))
						.filter((item) => !downqueue.map((d) => d.key).includes(item.key));

					const items = (this.tracked.studyplan = [
						...upqueue,
						...recommendedItems.filter((item) => item.recommendedorder !== undefined),
						...recommendedItems.filter((item) => item.pretest <= scoreThreshold),
						...recommendedItems.filter((item) => item.pretest > scoreThreshold),
						...downqueue
					]);

					window.hcdata.studyplan = items;

					// Clear changes
					this.changes = [];
					studyPlanToolChange({ items });
				};

				const { useML } = studyplan;
				const { completedOnce } = studyplan.ml || {};

				if (useML && completedOnce) {
					buildStudyplan();
				} else {
					buildStudyplan();
				}
			},

			getStudyPlan(this: StudyPlanAPI, items: StudyPlanItem[]): WNode[] {
				const quizzes = this.tracked.quizzes;
				const { content: c } = contentTrack.tracked;
				const contentMap = Object.assign({}, ...Object.keys(c).map((k) => mapKids(c[k])));
				const sp = items.filter((item) => item.state === 'promoted' || item.state === 'queued');
				return sp
					.map(({ key }) => {
						const {
							topichead: domain,
							completed,
							percent,
							title,
							pretest,
							isreadingstarted,
							isreadingcompleted,
							isquizcompleted
						} = contentMap[key];
						const topic = this.getTopic(key);
						const isquizstarted = quizzes && quizzes[getQuizId(key)]?.combined_status?.active;
						const card = w(TGFCard, {
							key,
							domain,
							completed,
							percent,
							title,
							topic,
							pretest,
							isreadingstarted,
							isreadingcompleted,
							isquizstarted,
							isquizcompleted,
							ispromoted: true,
							action: this.dequeue
						});
						return card;
					})
					.slice(0, 3);
			},

			getDomains(this: StudyPlanAPI): StudyPlanDomain[] {
				const quizzes = this.tracked.quizzes;
				const content = contentTrack.tracked.content;
				// Only grab the first 3 that are in either promoted or queued state (We dont want to accidentally grab any "demoted" topics if they remove everything)
				const promoted = this.tracked.studyplan
					.filter((item) => item.state === 'promoted' || item.state === 'queued')
					.slice(0, 3)
					.map((item) => item.key);
				return Object.keys(content).map((domain) => {
					const { title: lTitle = domain } = keymap.get(domain) || {};
					const title = capitalize(lTitle);
					const children = content[domain];
					let domainCompleted = 0;
					const topicsets = children.map((tgf) => {
						const {
							topichead: domain,
							topicset: key,
							completed,
							percent,
							title,
							pretest,
							isreadingstarted,
							isreadingcompleted,
							isquizcompleted
						} = tgf;
						const topic = this.getTopic(key);
						const isquizstarted = quizzes && quizzes[getQuizId(key)]?.combined_status?.active;
						const iscompleted = isreadingcompleted && isquizcompleted;
						// this should probably move into lcc-state
						domainCompleted += iscompleted ? 1 : 0;
						return {
							key,
							domain,
							completed,
							percent,
							title,
							topic,
							pretest,
							iscompleted,
							isreadingstarted,
							isreadingcompleted,
							isquizstarted,
							isquizcompleted,
							ispromotable: !promoted.includes(key),
							ispromoted: false,
							action: this.enqueue
						};
					});
					// sort completed to end of the queue
					const sortedtopicsets = orderBy(topicsets, 'iscompleted', 'asc');
					const cards = sortedtopicsets.map((topicset) => w(TGFCard, topicset));
					return { domain, title, cards, completed: domainCompleted };
				});
			},

			getTopic(this: StudyPlanAPI, key: string): TopicDetails {
				const { path } = keymap.get(key)!;
				const { topic } = pathmap.get(path)!;
				return topic.topicset;
			},

			enqueue(this: StudyPlanAPI, key: string): void {
				const state = 'promoted';
				const date = now();
				const pretest = lowScoreData[key];
				api.changes.push({ key, state, date, pretest });
				api.recalculate();
			},

			dequeue(this: StudyPlanAPI, key: string): void {
				const state = 'demoted';
				const date = null;
				const pretest = lowScoreData[key];
				api.changes.push({ key, state, date, pretest });
				api.recalculate();
			},

			calculate_motivations(this: StudyPlanAPI) {
				// caculate motivation
				const { date: motivateddate, is_show: is_show_motivation } = get(path('toolstate', 'motivateddate'));
				const motivationDaysThreshold = parseInt(motivation_days_threshold);
				const newdate = DateTime.now().setZone('local').toString();
				const today = newdate.split('T')[0];
				const last_motivated_date = DateTime.fromISO(motivateddate);
				if (is_show_motivation) {
					if (
						!isEmpty(motivateddate) &&
						Math.floor(DateTime.now().diff(last_motivated_date, 'days').days) > 0 &&
						Math.floor(DateTime.now().diff(last_motivated_date, 'days').days) < motivationDaysThreshold
					) {
						motivatedDateChange({ is_show: false });
					} else {
						motivatedDateChange({ date: today });
					}
				} else {
					if (
						isEmpty(motivateddate) ||
						Math.floor(DateTime.now().diff(last_motivated_date, 'days').days) >= motivationDaysThreshold
					) {
						motivatedDateChange({ date: today, is_show: true });
					}
				}
			},
			calculate_milestones(this: StudyPlanAPI) {
				const { milestone_date, is_show_milestone } = get(path('toolstate', 'motivateddate'));

				const newdate = DateTime.now().setZone('local').toString();
				const today = newdate.split('T')[0];

				if (isEmpty(milestone_date)) {
					const milestoneThreshold = parseInt(milestone_threshold);
					const content = contentTrack.tracked.content;
					const status = get(path('status'));
					let totalCount = 0;
					Object.keys(content).forEach((domain) => {
						content[domain].forEach((topicset) => {
							// Check if `isreadingstarted` or `isreadingcompleted` is true
							if (topicset.isreadingstarted || topicset.isreadingcompleted) {
								const topic = this.getTopic(topicset.topicset);
								if (topic && topic.children) {
									topic.children.forEach((topicgroup) => {
										if (
											topicgroup.topicgroup &&
											topicgroup.topicgroup.attributes &&
											topicgroup.topicgroup.attributes.key
										) {
											const topicstatus = status[topicgroup.topicgroup.attributes.key].data;
											if (topicstatus.complete === topicstatus.total) {
												totalCount += 1;
											}
										}
									});
								}
							}
						});
					});

					const quizzes = this.tracked.quizzes;
					const completedCount = Object.values(quizzes).filter(
						(quiz) => quiz.combined_status.complete === true
					).length;

					totalCount += completedCount;
					if (totalCount >= milestoneThreshold) {
						motivatedDateChange({ milestone_date: today, is_show_milestone: true });
					}
				} else {
					if (is_show_milestone) {
						const last_milestone_date = DateTime.fromISO(milestone_date);
						if (Math.floor(DateTime.now().diff(last_milestone_date, 'days').days) > 0) {
							motivatedDateChange({ is_show_milestone: false });
						}
					}
				}
			},

			getMotivations(this: StudyPlanAPI): WNode[] {
				api.motivation_cards_list = [];

				const close_motivation = () => {
					motivatedDateChange({ is_show: false });
					api.motivation_cards_list = [];
					api.calculate_motivations();
				};

				const close_milestone = () => {
					motivatedDateChange({ is_show_milestone: false });
					api.motivation_cards_list = [];
					api.calculate_milestones();
				};

				if (use_motivation_card) {
					api.calculate_motivations();
					const { is_show: is_show_motivation } = get(path('toolstate', 'motivateddate'));
					if (is_show_motivation) {
						api.motivation_cards_list.push(w(MotivationCard, { action: close_motivation }));
					}
				}
				if (use_milestone_card) {
					api.calculate_milestones();
					const { is_show_milestone } = get(path('toolstate', 'motivateddate'));
					if (is_show_milestone) {
						api.motivation_cards_list.push(
							w(MotivationCard, { is_milestone: true, action: close_milestone })
						);
					}
				}

				return api.motivation_cards_list;
			},

			calculate_extras(this: StudyPlanAPI) {
				const studyplan = get(path('toolstate', 'studyplan'));
				const { extra: currentExtra = [] } = studyplan;
				const { promoteable_additional_resources = '[]' } = resourceMessages;

				// Get cached earned state, or start async fetch
				let earned = icache.get('earned');
				if (earned === undefined) {
					icache.set('earned', false); // Temporary value to prevent multiple fetches

					(async () => {
						const posttest = get(path('posttest'));
						const userid = get(path('user_id'));

						earned = posttest && posttest.passed ? true : await check_rpc_passed(userid);
						icache.set('earned', earned);

						// Re-run extras calculation once earned is ready
						this.calculate_extras();
					})();

					return;
				}

				const additionalResourceMSP = JSON.parse(promoteable_additional_resources);
				const newExtraItems = [];
				for (const itemkey of additionalResourceMSP) {
					const extraItem = currentExtra.find((item: StudyPlanExtraItem) => item.key === itemkey);
					if (!extraItem) {
						if (itemkey !== 'certificateCard' || earned) {
							newExtraItems.push({ key: itemkey, state: 'promoted', type: 'resource', date: now() });
						}
					}
				}
				const newExtra = [...currentExtra, ...newExtraItems];

				const extra = orderBy(dedup(newExtra), 'date', 'desc');
				studyPlanToolChange({ extra });
			},

			getExtraCardInfo(this: StudyPlanAPI) {
				api.calculate_extras();

				const studyplan = get(path('toolstate', 'studyplan'));
				const { extra = [] } = studyplan;
				const extraCardInfo = extra.reduce((prev: FormattingReplacements, item: StudyPlanExtraItem) => {
					prev[`${item.key}_promoted`] = item.state === 'promoted' ? 'true' : 'false';
					prev[`${item.key}_ispromotable`] = 'true';
					return prev;
				}, {});
				return extraCardInfo;
			},

			getExtraCards(this: StudyPlanAPI): WNode[] {
				api.extra_cards_list = [];

				subscribe('tool:studyplan:modify', (name, changes) => {
					const studyplan = get(path('toolstate', 'studyplan'));
					const { extra = [] } = studyplan;
					console.log(`${name} event handled`);
					const current = extra.filter((item: StudyPlanExtraItem) => item.key !== changes.key);
					const changed: StudyPlanExtraItem[] = [
						{
							key: changes.key,
							state: changes.action === 'add' ? 'promoted' : 'demoted',
							type: changes.type,
							date: now()
						}
					];
					studyPlanToolChange({ extra: [...current, ...changed] });
					api.extra_cards_list = [];
					api.calculate_extras();
				});

				const studyplan = get(path('toolstate', 'studyplan'));
				const extraCardInfo = api.getExtraCardInfo();
				const { extra = [] } = studyplan;

				const extraPromotedItems: StudyPlanExtraItem[] = extra.filter(
					(item: StudyPlanExtraItem) => item.state === 'promoted'
				);
				api.extra_cards_list = extraPromotedItems.reduce((extraCards: any[], item: StudyPlanExtraItem) => {
					if (item.type === 'resource') {
						extraCards.push(
							w(ResourceParser as any, {
								messages: resourceMessages,
								topicid: item.key,
								key: `resource_${item.key}`,
								formatting: extraCardInfo,
								preformat: true
							})
						);
					}
					return extraCards;
				}, []);

				if (has('use-ml-study-plan') && studyplan.ml.inFlight) {
					api.extra_cards_list.push(w(LoadingCard, { xqa: 'studyplan-loading' }));
				}

				return api.extra_cards_list;
			},

			async getQuizzes(this: StudyPlanAPI): Promise<QuizStateMap> {
				const site = window.hcstatic.portal_site as string;
				const quizids: string[] = [...tgfkeys].map((tgfkey) => getQuizId(tgfkey));
				const quizzes = (this.tracked.quizzes = await svc.quiz_state({ site, quizids }));
				this.tracked.studyplan = [];
				this.recalculate();
				return quizzes;
			}
		};

		api.setup();
		return api;
	}
);
